"use client";

import * as React from "react";
import { useCallback, useRef, useEffect } from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSwipeGesture } from "../../../hooks/gallery-lightbox/use-swipe-gesture";
import { useKeyboardNavigation } from "../../../hooks/gallery-lightbox/use-keyboard-navigation";

export type GalleryImageT = {
  src: string;
  alt?: string;
  title?: string;
};

type GalleryLightboxMobilePropsT = {
  images: GalleryImageT[];
  initialIndex: number;
  onClose: () => void;
};

/**
 * Mobile-optimized gallery lightbox component
 * Features swipe gestures for navigation and fullscreen image display
 */
export function GalleryLightboxMobile({
  images,
  initialIndex,
  onClose,
}: GalleryLightboxMobilePropsT) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const swipeHandlers = useSwipeGesture(handleNext, handlePrevious);

  useKeyboardNavigation({
    isEnabled: true,
    onEscape: onClose,
    onArrowLeft: handlePrevious,
    onArrowRight: handleNext,
  });

  // Auto-scroll thumbnail into view when currentIndex changes
  useEffect(() => {
    const currentThumbnail = thumbnailRefs.current[currentIndex];
    currentThumbnail?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [currentIndex]);

  return (
    <div className="animate-in fade-in fixed inset-0 z-[150] flex flex-col bg-black duration-300">
      {/* Header with close button and counter */}
      <div className="flex items-center justify-between p-4">
        <div className="rounded-full bg-white/10 px-3 py-1 text-sm text-white backdrop-blur">
          {currentIndex + 1} / {images.length}
        </div>

        <button
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-all hover:bg-white/20 active:scale-95"
          aria-label="Close gallery"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main image area with swipe gestures - NO key prop to prevent re-mounting */}
      <div
        className="relative flex flex-1 items-center justify-center"
        {...swipeHandlers}
      >
        <div className="relative h-full w-full">
          {images.map((image, index) => (
            <div
              key={image.src}
              className={cn(
                "absolute inset-0 transition-opacity duration-300",
                index === currentIndex
                  ? "opacity-100"
                  : "pointer-events-none opacity-0"
              )}
            >
              <Image
                src={image.src}
                alt={image.alt || ""}
                fill
                className="object-contain"
                sizes="100vw"
                priority={
                  index === currentIndex ||
                  index === currentIndex - 1 ||
                  index === currentIndex + 1
                }
                quality={90}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Thumbnail navigation */}
      {images.length > 1 && (
        <div className="flex justify-start gap-3.5 overflow-x-auto scroll-smooth bg-black/50 px-3 py-10 backdrop-blur">
          {images.map((image, index) => (
            <button
              key={image.src}
              ref={(el) => {
                thumbnailRefs.current[index] = el;
              }}
              onClick={() => handleThumbnailClick(index)}
              className={cn(
                "relative h-14 w-20 flex-shrink-0 overflow-hidden rounded-md transition-all duration-300",
                currentIndex === index
                  ? "scale-110 opacity-100 ring-2 ring-white ring-offset-2 ring-offset-black"
                  : "scale-100 opacity-60 hover:opacity-100"
              )}
              aria-label={`Go to image ${index + 1}`}
            >
              <Image
                src={image.src}
                alt={image.alt || `Thumbnail ${index + 1}`}
                fill
                className="object-cover"
                sizes="80px"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
